import { useState, useEffect, useRef } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { AlertCircle, ChevronDown, Check, Plus, X, ArrowDownLeft, ArrowUpRight, Search, Globe, TrendingUp, Hash, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { countriesSorted } from '@/data/countries';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';
import { FlagImg } from '@/components/ui/CountryFlag';

const amountTiers = [
  { value: 'under_25k', label: 'Under €25,000', description: 'Small volume' },
  { value: '25k_100k', label: '€25K – €100K', description: 'Medium volume' },
  { value: '100k_500k', label: '€100K – €500K', description: 'High volume' },
  { value: '500k_1m', label: '€500K – €1M', description: 'Very high volume' },
  { value: '1m_5m', label: '€1M – €5M', description: 'Corporate volume' },
  { value: 'over_5m', label: 'Over €5,000,000', description: 'Enterprise volume' },
];

const sourceTypes = [
  { value: 'business_revenue', label: 'Business Revenue', description: 'Income from sales/services' },
  { value: 'investment_returns', label: 'Investment Returns', description: 'Returns from investments' },
  { value: 'loans', label: 'Loans', description: 'Bank or private loans' },
  { value: 'grants', label: 'Grants/Subsidies', description: 'Government or private grants' },
  { value: 'shareholder_funding', label: 'Shareholder Funding', description: 'Capital from shareholders' },
  { value: 'intercompany', label: 'Intercompany Transfers', description: 'Transfers between group entities' },
  { value: 'other', label: 'Other', description: 'Specify other sources' },
];

const destinationTypes = [
  { value: 'suppliers', label: 'Suppliers/Vendors', description: 'Payment for goods/services' },
  { value: 'payroll', label: 'Payroll', description: 'Employee salaries' },
  { value: 'taxes', label: 'Taxes', description: 'Tax payments to authorities' },
  { value: 'investments', label: 'Investments', description: 'Business investments' },
  { value: 'dividends', label: 'Dividends', description: 'Shareholder distributions' },
  { value: 'intercompany', label: 'Intercompany Transfers', description: 'Transfers to group entities' },
  { value: 'loan_repayments', label: 'Loan Repayments', description: 'Repaying loans' },
  { value: 'other', label: 'Other', description: 'Specify other destinations' },
];

const paymentCountTiers = [
  { value: 'under_10', label: 'Under 10', description: 'Very low frequency' },
  { value: '10_50', label: '10 – 50', description: 'Low frequency' },
  { value: '50_100', label: '50 – 100', description: 'Medium frequency' },
  { value: '100_500', label: '100 – 500', description: 'High frequency' },
  { value: '500_1000', label: '500 – 1,000', description: 'Very high frequency' },
  { value: 'over_1000', label: 'Over 1,000', description: 'Ultra high frequency' },
];

interface PaymentFlow {
  types: string[];
  otherDescription?: string;
  monthlyVolume: string;
  monthlyCount: string;
  countries: { code: string; name: string; flag: string }[];
}

export function CorporatePaymentFlowsStep() {
  const { data, updateData, goToNextStep } = useOnboarding();
  const { canActivate } = useScrollToActivate();
  const company = data.corporateCompany;

  const [incomingFlow, setIncomingFlow] = useState<PaymentFlow>(() => ({
    types: data.corporatePaymentFlows?.incoming?.types || [],
    otherDescription: data.corporatePaymentFlows?.incoming?.otherDescription || '',
    monthlyVolume: data.corporatePaymentFlows?.incoming?.monthlyVolume || '',
    monthlyCount: data.corporatePaymentFlows?.incoming?.monthlyCount || '',
    countries: data.corporatePaymentFlows?.incoming?.countries || [],
  }));

  const [outgoingFlow, setOutgoingFlow] = useState<PaymentFlow>(() => ({
    types: data.corporatePaymentFlows?.outgoing?.types || [],
    otherDescription: data.corporatePaymentFlows?.outgoing?.otherDescription || '',
    monthlyVolume: data.corporatePaymentFlows?.outgoing?.monthlyVolume || '',
    monthlyCount: data.corporatePaymentFlows?.outgoing?.monthlyCount || '',
    countries: data.corporatePaymentFlows?.outgoing?.countries || [],
  }));

  // ── Business Questionnaire (engine reads corporateCompany.businessQuestionnaire) ──
  const existingBQ = data.corporateCompany?.businessQuestionnaire;
  const [bqVolume, setBqVolume] = useState<string>(() => existingBQ?.expectedMonthlyVolume || '');
  const [bqTurnover, setBqTurnover] = useState<string>(() => existingBQ?.expectedYearlyTurnover || '');
  const [bqHoldsClientFunds, setBqHoldsClientFunds] = useState<boolean | null>(() =>
    typeof existingBQ?.holdsClientFunds === 'boolean' ? existingBQ.holdsClientFunds : null
  );
  const [bqCountries, setBqCountries] = useState<string[]>(() => existingBQ?.countriesOfOperation || []);
  const [bqSourceOfFunds, setBqSourceOfFunds] = useState<string[]>(() => existingBQ?.sourceOfFunds || []);
  const [bqPurpose, setBqPurpose] = useState<string[]>(() => existingBQ?.purposeOfAccount || []);
  const [bqCountrySearch, setBqCountrySearch] = useState('');

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-muted-foreground">Company data not found.</p>
        </div>
      </div>
    );
  }

  const handleContinue = () => {
    updateData({
      corporatePaymentFlows: {
        incoming: incomingFlow,
        outgoing: outgoingFlow,
      },
      // Merge into corporateCompany.businessQuestionnaire — the exact path the
      // calculate-corporate-risk engine reads.
      corporateCompany: {
        ...company,
        businessQuestionnaire: {
          ...(existingBQ || {}),
          expectedMonthlyVolume: bqVolume,
          expectedYearlyTurnover: bqTurnover,
          holdsClientFunds: bqHoldsClientFunds === true,
          countriesOfOperation: bqCountries,
          sourceOfFunds: bqSourceOfFunds,
          purposeOfAccount: bqPurpose,
          // preserve required type fields if absent
          sourceOfWealth: existingBQ?.sourceOfWealth || [],
        },
      },
    });
    goToNextStep();
  };

  const isIncomingValid = incomingFlow.types.length > 0 && 
    incomingFlow.monthlyVolume && 
    incomingFlow.monthlyCount &&
    incomingFlow.countries.length >= 1 &&
    (!incomingFlow.types.includes('other') || (incomingFlow.otherDescription?.trim().length || 0) > 0);

  const isOutgoingValid = outgoingFlow.types.length > 0 && 
    outgoingFlow.monthlyVolume && 
    outgoingFlow.monthlyCount &&
    outgoingFlow.countries.length >= 1 &&
    (!outgoingFlow.types.includes('other') || (outgoingFlow.otherDescription?.trim().length || 0) > 0);

  const isQuestionnaireValid =
    !!bqVolume &&
    !!bqTurnover &&
    bqHoldsClientFunds !== null &&
    bqCountries.length >= 1 &&
    bqSourceOfFunds.length >= 1 &&
    bqPurpose.length >= 1;

  const isFormValid = isIncomingValid && isOutgoingValid && isQuestionnaireValid;


  const incomingProgress = [
    incomingFlow.types.length > 0,
    !!incomingFlow.monthlyVolume,
    !!incomingFlow.monthlyCount,
    incomingFlow.countries.length >= 1,
  ].filter(Boolean).length;

  const outgoingProgress = [
    outgoingFlow.types.length > 0,
    !!outgoingFlow.monthlyVolume,
    !!outgoingFlow.monthlyCount,
    outgoingFlow.countries.length >= 1,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background flex flex-col pt-20 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        <div className="animate-fade-in-up">
          {/* Header */}
          <div className="text-center mb-5">
            <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-7 h-7 text-accent" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
              Payment Flows
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Tell us about your expected fund movements
            </p>
          </div>

          {/* Guidance Banner */}
          <div className="rounded-xl border border-accent/20 bg-accent/5 p-3.5 mb-6 flex gap-3 items-start">
            <Info className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <span className="text-foreground font-medium">Why do we need this?</span> Understanding your payment patterns helps us configure your account with the right limits, compliance checks, and optimal routing for your transactions.
            </div>
          </div>

          {/* ─── SECTION 1: INCOMING ─── */}
          <FlowSection
            direction="incoming"
            icon={<ArrowDownLeft className="w-5 h-5" />}
            title="Incoming Payments"
            subtitle="Where your funds come from"
            accentColor="emerald"
            flow={incomingFlow}
            setFlow={setIncomingFlow}
            types={sourceTypes}
            typeLabel="Source of Funds"
            typeHint="Select all sources that apply to your business"
            isValid={!!isIncomingValid}
            progress={incomingProgress}
          />

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" />
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className={`w-2 h-2 rounded-full transition-colors ${isIncomingValid ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
              <span>Incoming {isIncomingValid ? 'complete' : 'pending'}</span>
              <span className="text-muted-foreground/40">•</span>
              <span>Outgoing {isOutgoingValid ? 'complete' : 'pending'}</span>
              <div className={`w-2 h-2 rounded-full transition-colors ${isOutgoingValid ? 'bg-blue-500' : 'bg-muted-foreground/30'}`} />
            </div>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* ─── SECTION 2: OUTGOING ─── */}
          <FlowSection
            direction="outgoing"
            icon={<ArrowUpRight className="w-5 h-5" />}
            title="Outgoing Payments"
            subtitle="Where your funds are sent"
            accentColor="blue"
            flow={outgoingFlow}
            setFlow={setOutgoingFlow}
            types={destinationTypes}
            typeLabel="Payment Destinations"
            typeHint="Select all destinations that apply to your business"
            isValid={!!isOutgoingValid}
            progress={outgoingProgress}
          />

          {/* ─── SECTION 3: BUSINESS QUESTIONNAIRE ─── */}
          {/* These fields are read directly by calculate-corporate-risk under
              corporateCompany.businessQuestionnaire. Keep enum keys exact. */}
          <div className="mt-8 rounded-2xl border border-border bg-card p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-1">
              <Hash className="w-5 h-5 text-accent" />
              <h2 className="text-base font-semibold text-foreground">Business Profile</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-5">
              A few additional questions used by Compliance to risk-rate your business.
            </p>

            {/* Expected monthly volume */}
            <label className="block text-sm font-medium text-foreground mb-2">
              Expected monthly transaction volume
            </label>
            <div className="grid grid-cols-1 gap-2 mb-5">
              {[
                { v: 'under_10k',    label: 'Under €10,000' },
                { v: '10k_100k',     label: '€10,000 – €100,000' },
                { v: '100k_500k',    label: '€100,000 – €500,000' },
                { v: '500k_1m',      label: '€500,000 – €1,000,000' },
                { v: 'over_1m',      label: 'Over €1,000,000' },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setBqVolume(o.v)}
                  className={`w-full text-left p-3 rounded-xl border-2 text-sm transition-all ${
                    bqVolume === o.v
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {/* Expected yearly turnover */}
            <label className="block text-sm font-medium text-foreground mb-2">
              Expected yearly turnover
            </label>
            <div className="grid grid-cols-1 gap-2 mb-5">
              {[
                { v: 'under_100k',  label: 'Under €100,000' },
                { v: '100k_500k',   label: '€100,000 – €500,000' },
                { v: '500k_1m',     label: '€500,000 – €1,000,000' },
                { v: '1m_5m',       label: '€1M – €5M' },
                { v: '5m_10m',      label: '€5M – €10M' },
                { v: 'over_10m',    label: 'Over €10M' },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setBqTurnover(o.v)}
                  className={`w-full text-left p-3 rounded-xl border-2 text-sm transition-all ${
                    bqTurnover === o.v
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {/* Holds client funds */}
            <label className="block text-sm font-medium text-foreground mb-2">
              Will the business hold third-party client funds?
            </label>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {[
                { v: true,  label: 'Yes' },
                { v: false, label: 'No' },
              ].map((o) => (
                <button
                  key={String(o.v)}
                  type="button"
                  onClick={() => setBqHoldsClientFunds(o.v)}
                  className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    bqHoldsClientFunds === o.v
                      ? 'border-accent bg-accent/10 text-foreground'
                      : 'border-border bg-muted/20 hover:border-muted-foreground/30 text-foreground'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {/* Countries of operation */}
            <label className="block text-sm font-medium text-foreground mb-2">
              Countries of operation
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              Where the business actively trades, sources, or sells. Select all that apply.
            </p>
            {bqCountries.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {bqCountries.map((code) => {
                  const c = countriesSorted.find((x) => x.code === code);
                  return (
                    <Badge
                      key={code}
                      variant="secondary"
                      className="gap-1 pl-2 pr-1 py-1 text-xs"
                    >
                      {c?.flag} {c?.name || code}
                      <button
                        type="button"
                        onClick={() => setBqCountries((prev) => prev.filter((x) => x !== code))}
                        className="ml-1 rounded-full hover:bg-background/40 p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
            <div className="relative mb-5">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={bqCountrySearch}
                onChange={(e) => setBqCountrySearch(e.target.value)}
                placeholder="Add a country…"
                className="pl-9 h-10 text-sm"
              />
              {bqCountrySearch.trim().length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-xl border border-border bg-popover shadow-lg">
                  {countriesSorted
                    .filter(
                      (c) =>
                        !bqCountries.includes(c.code) &&
                        (c.name.toLowerCase().includes(bqCountrySearch.toLowerCase()) ||
                          c.code.toLowerCase().includes(bqCountrySearch.toLowerCase()))
                    )
                    .slice(0, 30)
                    .map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => {
                          setBqCountries((prev) => [...prev, c.code]);
                          setBqCountrySearch('');
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted/40 flex items-center gap-2"
                      >
                        <span>{c.flag}</span>
                        <span>{c.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{c.code}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* Source of funds */}
            <label className="block text-sm font-medium text-foreground mb-2">
              Source of funds
            </label>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {[
                { v: 'business_revenue',   label: 'Business revenue' },
                { v: 'investment_returns', label: 'Investment returns' },
                { v: 'loans',              label: 'Loans' },
                { v: 'grants',             label: 'Grants / subsidies' },
                { v: 'shareholder_funding',label: 'Shareholder funding' },
                { v: 'intercompany',       label: 'Intercompany transfers' },
                { v: 'asset_sale',         label: 'Sale of assets' },
                { v: 'other',              label: 'Other' },
              ].map((o) => {
                const selected = bqSourceOfFunds.includes(o.v);
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() =>
                      setBqSourceOfFunds((prev) =>
                        prev.includes(o.v) ? prev.filter((x) => x !== o.v) : [...prev, o.v]
                      )
                    }
                    className={`p-2.5 rounded-xl border-2 text-xs font-medium text-left transition-all ${
                      selected
                        ? 'border-accent bg-accent/10'
                        : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>

            {/* Purpose of account */}
            <label className="block text-sm font-medium text-foreground mb-2">
              Purpose of the account
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: 'receive_payments',  label: 'Receive customer payments' },
                { v: 'pay_suppliers',     label: 'Pay suppliers' },
                { v: 'payroll',           label: 'Payroll' },
                { v: 'fx',                label: 'FX / multi-currency' },
                { v: 'treasury',          label: 'Treasury management' },
                { v: 'investments',       label: 'Investments' },
                { v: 'intercompany',      label: 'Intercompany transfers' },
                { v: 'other',             label: 'Other' },
              ].map((o) => {
                const selected = bqPurpose.includes(o.v);
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() =>
                      setBqPurpose((prev) =>
                        prev.includes(o.v) ? prev.filter((x) => x !== o.v) : [...prev, o.v]
                      )
                    }
                    className={`p-2.5 rounded-xl border-2 text-xs font-medium text-left transition-all ${
                      selected
                        ? 'border-accent bg-accent/10'
                        : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>


      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleContinue}
            disabled={!isFormValid || !canActivate}
            className="w-full h-14 text-base font-semibold rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FlowSection — self-contained section for one direction
   ═══════════════════════════════════════════════════════════════ */

interface FlowSectionProps {
  direction: 'incoming' | 'outgoing';
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  accentColor: 'emerald' | 'blue';
  flow: PaymentFlow;
  setFlow: React.Dispatch<React.SetStateAction<PaymentFlow>>;
  types: typeof sourceTypes;
  typeLabel: string;
  typeHint: string;
  isValid: boolean;
  progress: number;
}

const COLOR_MAP = {
  emerald: {
    iconBg: 'bg-emerald-500/10',
    iconText: 'text-emerald-500',
    headerBorder: 'border-emerald-500/20',
    headerBg: 'bg-emerald-500/5',
    selectedBorder: 'border-emerald-500',
    selectedBg: 'bg-emerald-500/10',
    selectedGlow: 'shadow-[0_0_12px_rgba(16,185,129,0.1)]',
    checkBorder: 'border-emerald-500',
    checkBg: 'bg-emerald-500',
    badgeBorder: 'border-emerald-500/40',
    badgeText: 'text-emerald-500',
    progressBg: 'bg-emerald-500',
    countryPrimary: 'bg-emerald-500/15 border-emerald-500/40',
    countryLabel: 'text-emerald-500',
    pickerActiveBorder: 'border-emerald-500/50',
    pickerActiveBg: 'bg-emerald-500/5',
  },
  blue: {
    iconBg: 'bg-blue-500/10',
    iconText: 'text-blue-500',
    headerBorder: 'border-blue-500/20',
    headerBg: 'bg-blue-500/5',
    selectedBorder: 'border-blue-500',
    selectedBg: 'bg-blue-500/10',
    selectedGlow: 'shadow-[0_0_12px_rgba(59,130,246,0.1)]',
    checkBorder: 'border-blue-500',
    checkBg: 'bg-blue-500',
    badgeBorder: 'border-blue-500/40',
    badgeText: 'text-blue-500',
    progressBg: 'bg-blue-500',
    countryPrimary: 'bg-blue-500/15 border-blue-500/40',
    countryLabel: 'text-blue-500',
    pickerActiveBorder: 'border-blue-500/50',
    pickerActiveBg: 'bg-blue-500/5',
  },
};

function FlowSection({
  direction,
  icon,
  title,
  subtitle,
  accentColor,
  flow,
  setFlow,
  types,
  typeLabel,
  typeHint,
  isValid,
  progress,
}: FlowSectionProps) {
  const c = COLOR_MAP[accentColor];
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const countryPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (countryPickerRef.current && !countryPickerRef.current.contains(e.target as Node)) {
        setShowCountryPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCountries = countriesSorted.filter(cc =>
    cc.name.toLowerCase().includes(countrySearch.toLowerCase()) &&
    !flow.countries.some(sel => sel.code === cc.code)
  );

  const addCountry = (country: { code: string; name: string; flag: string }) => {
    if (flow.countries.length >= 3) return;
    if (flow.countries.some(c2 => c2.code === country.code)) return;
    setFlow(prev => ({ ...prev, countries: [...prev.countries, country] }));
    setShowCountryPicker(false);
    setCountrySearch('');
  };

  const removeCountry = (code: string) => {
    setFlow(prev => ({ ...prev, countries: prev.countries.filter(c2 => c2.code !== code) }));
  };

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className={`rounded-2xl border ${c.headerBorder} ${c.headerBg} p-4 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${c.iconBg} flex items-center justify-center ${c.iconText}`}>
            {icon}
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground flex items-center gap-2">
              {title}
              {isValid && (
                <span className={`w-5 h-5 rounded-full ${c.checkBg} flex items-center justify-center`}>
                  <Check className="w-3 h-3 text-white" />
                </span>
              )}
            </h2>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-xs font-medium text-muted-foreground">{progress}/4</span>
          <div className="flex gap-1 mt-1">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className={`w-4 h-1 rounded-full transition-colors ${i < progress ? c.progressBg : 'bg-muted-foreground/20'}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Type Selection */}
      <div className={`rounded-2xl border ${flow.types.length > 0 ? c.headerBorder : 'border-border'} bg-card/50 p-4 transition-colors`}>
        <div className="flex items-center justify-between mb-1">
          <p className="font-semibold text-sm text-foreground">{typeLabel}</p>
          {flow.types.length > 0 && (
            <Badge variant="outline" className={`text-[10px] ${c.badgeBorder} ${c.badgeText}`}>
              {flow.types.length} selected
            </Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">{typeHint}</p>
        <div className="space-y-2">
          {types.map((type) => {
            const isSelected = flow.types.includes(type.value);
            return (
              <button
                key={type.value}
                onClick={() => setFlow(prev => ({
                  ...prev,
                  types: prev.types.includes(type.value)
                    ? prev.types.filter(t => t !== type.value)
                    : [...prev.types, type.value]
                }))}
                className={`w-full p-3.5 rounded-xl border transition-all flex items-center justify-between text-left group ${
                  isSelected
                    ? `${c.selectedBorder} ${c.selectedBg} ${c.selectedGlow}`
                    : 'border-border/60 bg-muted/10 hover:border-muted-foreground/30 hover:bg-muted/20'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium block ${isSelected ? 'text-foreground' : 'text-foreground/80'}`}>
                    {type.label}
                  </span>
                  <span className="text-xs text-muted-foreground">{type.description}</span>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-3 transition-all ${
                  isSelected 
                    ? `${c.checkBorder} ${c.checkBg}` 
                    : 'border-muted-foreground/30 group-hover:border-muted-foreground/50'
                }`}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
              </button>
            );
          })}
        </div>

        {flow.types.includes('other') && (
          <div className="mt-3">
            <Input
              placeholder="Please describe other sources/destinations..."
              value={flow.otherDescription || ''}
              onChange={(e) => setFlow(prev => ({ ...prev, otherDescription: e.target.value }))}
              className="h-11 text-sm rounded-xl bg-muted/20"
            />
          </div>
        )}
      </div>

      {/* Monthly Volume */}
      <div className={`rounded-2xl border ${flow.monthlyVolume ? c.headerBorder : 'border-border'} bg-card/50 p-4 transition-colors`}>
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className={`w-4 h-4 ${c.iconText}`} />
          <p className="font-semibold text-sm text-foreground">Expected Monthly Volume</p>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">Total value of {direction === 'incoming' ? 'incoming' : 'outgoing'} payments per month</p>
        <div className="grid grid-cols-2 gap-2">
          {amountTiers.map((tier) => {
            const isSelected = flow.monthlyVolume === tier.value;
            return (
              <button
                key={tier.value}
                onClick={() => setFlow(prev => ({ ...prev, monthlyVolume: tier.value }))}
                className={`p-3 rounded-xl border transition-all text-left ${
                  isSelected
                    ? `${c.selectedBorder} ${c.selectedBg} ${c.selectedGlow}`
                    : 'border-border/60 bg-muted/10 hover:border-muted-foreground/30 hover:bg-muted/20'
                }`}
              >
                <span className={`text-xs font-semibold block ${isSelected ? 'text-foreground' : 'text-foreground/80'}`}>
                  {tier.label}
                </span>
                <span className="text-[10px] text-muted-foreground">{tier.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Monthly Transaction Count */}
      <div className={`rounded-2xl border ${flow.monthlyCount ? c.headerBorder : 'border-border'} bg-card/50 p-4 transition-colors`}>
        <div className="flex items-center gap-2 mb-1">
          <Hash className={`w-4 h-4 ${c.iconText}`} />
          <p className="font-semibold text-sm text-foreground">Expected Monthly Transactions</p>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">Number of individual {direction === 'incoming' ? 'incoming' : 'outgoing'} transactions</p>
        <div className="grid grid-cols-2 gap-2">
          {paymentCountTiers.map((tier) => {
            const isSelected = flow.monthlyCount === tier.value;
            return (
              <button
                key={tier.value}
                onClick={() => setFlow(prev => ({ ...prev, monthlyCount: tier.value }))}
                className={`p-3 rounded-xl border transition-all text-left ${
                  isSelected
                    ? `${c.selectedBorder} ${c.selectedBg} ${c.selectedGlow}`
                    : 'border-border/60 bg-muted/10 hover:border-muted-foreground/30 hover:bg-muted/20'
                }`}
              >
                <span className={`text-xs font-semibold block ${isSelected ? 'text-foreground' : 'text-foreground/80'}`}>
                  {tier.label}
                </span>
                <span className="text-[10px] text-muted-foreground">{tier.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Countries */}
      <div className={`rounded-2xl border ${flow.countries.length >= 1 ? c.headerBorder : 'border-border'} bg-card/50 p-4 transition-colors`}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Globe className={`w-4 h-4 ${c.iconText}`} />
            <p className="font-semibold text-sm text-foreground">Main Countries</p>
          </div>
          <Badge 
            variant={flow.countries.length >= 1 ? "default" : "secondary"} 
            className={`text-xs ${flow.countries.length >= 1 ? `${c.checkBg} text-white` : ''}`}
          >
            {flow.countries.length}/3
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">Where {direction === 'incoming' ? 'payments originate from' : 'payments are sent to'} — 1 primary + up to 2 optional</p>

        {flow.countries.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {flow.countries.map((country, idx) => (
              <div
                key={country.code}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  idx === 0
                    ? `${c.countryPrimary} text-foreground`
                    : 'bg-muted/30 border border-border text-foreground/80'
                }`}
              >
                <FlagImg code={country.code} size="sm" />
                <span>{country.name}</span>
                {idx === 0 && (
                  <span className={`text-[9px] uppercase tracking-wider ${c.countryLabel} font-semibold`}>Primary</span>
                )}
                <button
                  onClick={() => removeCountry(country.code)}
                  className="hover:text-destructive transition-colors ml-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {flow.countries.length < 3 && (
          <div className="relative" ref={countryPickerRef}>
            <button
              onClick={() => setShowCountryPicker(!showCountryPicker)}
              className={`w-full h-11 px-3 rounded-xl border transition-all flex items-center justify-between text-sm ${
                showCountryPicker 
                  ? `${c.pickerActiveBorder} ${c.pickerActiveBg}` 
                  : 'border-border/60 bg-muted/10 hover:border-muted-foreground/30'
              }`}
            >
              <span className="text-muted-foreground flex items-center gap-2">
                <Plus className="w-4 h-4" />
                {flow.countries.length === 0 ? 'Add primary country' : 'Add optional country'}
              </span>
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${showCountryPicker ? 'rotate-180' : ''}`} />
            </button>

            {showCountryPicker && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-popover rounded-xl shadow-2xl border border-border z-[100] overflow-hidden animate-fade-in-up">
                <div className="p-2.5 border-b border-border/50">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search country..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-muted/50 border border-border/50 focus:outline-none focus:border-accent/50 text-foreground placeholder:text-muted-foreground transition-colors"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto overscroll-contain">
                  {filteredCountries.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-6">No countries found</p>
                  )}
                  {filteredCountries.slice(0, 50).map((cc) => (
                    <button
                      key={cc.code}
                      onClick={() => addCountry({ code: cc.code, name: cc.name, flag: cc.flag })}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-accent/10 transition-colors text-left text-foreground group"
                    >
                      <FlagImg code={cc.code} size="md" />
                      <span className="text-sm flex-1 font-medium group-hover:text-accent transition-colors">{cc.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
